/**
 * TemplateCompareTab — two templates side by side before adopting one. Demo page: /ui-elements/cards (two cards in a
 * grid). The other template is picked from the library, not typed.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GitCompare } from 'lucide-react';
import { getTemplateOptions, listTemplatesOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Callout, Card, EmptyState, Skeleton, describeQueryError } from '../../../components/ui/index.js';
import type { AgentTemplate } from '../../../generated/api/types.gen.js';
import { FilterSelect, sentenceCase } from '../../../components/ui/FilterSelect.js';
import { FactList, FactRow, TokenList, formatPercent } from './detail-parts.js';

const CompareColumn: React.FC<{ template: AgentTemplate }> = ({ template }) => (
  <div className="space-y-4 rounded-lg border p-4">
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-sm font-semibold text-heading">{template.title}</h3>
      <Badge variant="info">Version {template.version ?? 1}</Badge>
    </div>
    <FactList>
      <FactRow label="Quality" value={formatPercent(template.avgQualityScore)} />
      <FactRow label="Permission tier" value={sentenceCase(template.permissionTier ?? 'observer')} />
      <FactRow label="Tools" value={template.tools?.length ?? 0} />
      <FactRow label="Workflow steps" value={template.usageImpact?.workflowStepCount ?? 0} />
    </FactList>
    <TokenList
      label="Model capabilities"
      values={template.compatibility?.modelCapabilities ?? []}
      empty="None declared."
    />
  </div>
);

export const TemplateCompareTab: React.FC<{ base: AgentTemplate }> = ({ base }) => {
  const [otherSlug, setOtherSlug] = useState('');
  const listQuery = useQuery(listTemplatesOptions({ query: { includeDisabled: true } }));
  const otherQuery = useQuery({
    ...getTemplateOptions({ path: { templateSlug: otherSlug } }),
    enabled: otherSlug !== '',
  });
  const choices = (listQuery.data?.data ?? [])
    .filter((template) => template.slug !== base.slug)
    .map((template) => ({ value: template.slug, label: template.title }));

  return (
    <Card
      title="Compare templates"
      actions={
        <FilterSelect
          label="Compare with"
          anyLabel="Choose a template"
          value={otherSlug}
          options={choices}
          onChange={setOtherSlug}
        />
      }
    >
      {otherSlug === '' ? (
        <EmptyState
          icon={GitCompare}
          title="Pick a template to compare"
          description="Compare permissions, tools, quality and usage before adopting another template."
        />
      ) : otherQuery.isError ? (
        <Callout tone="danger" title="Could not load that template">
          {describeQueryError(otherQuery.error, 'template')}
        </Callout>
      ) : otherQuery.data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <CompareColumn template={base} />
          <CompareColumn template={otherQuery.data.template} />
        </div>
      ) : (
        <Skeleton height={160} />
      )}
    </Card>
  );
};
